import { Injectable, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { map } from 'rxjs/operators';
import { CategorizationRuleService, type CategorizationRuleSuggestion } from '../services/categorization-rule.service';
import { SessionService } from '../services/session.service';
import { findSuggestionForTransaction, type RuleBackfillCandidate } from './transaction-rule-display';

/**
 * One shared read of the active suggestion queue for every surface that only needs
 * a count or a per-transaction match: the home attention row, the Transactions
 * overflow menu badge, and the row chip. The query is only enabled behind the
 * `transaction-rules` flag so unflagged accounts never trigger suggestion generation.
 */
@Injectable({ providedIn: 'root' })
export class TransactionRuleSuggestionLookupService {
  private readonly categorizationRuleService = inject(CategorizationRuleService);
  private readonly sessionService = inject(SessionService);

  // Read the live session rather than the one-shot helper so refreshes, account
  // switches, and an admin toggling the flag are all reflected without a reload.
  readonly featureEnabled = toSignal(
    this.sessionService.session$.pipe(map(session => !!session?.enabledFeatures?.includes('transaction-rules'))),
    { initialValue: false },
  );

  private readonly suggestionsQuery = injectQuery(() => ({
    ...this.categorizationRuleService.suggestionsQueryOptions('ACTIVE'),
    enabled: this.featureEnabled(),
  }));

  readonly activeSuggestions = computed<CategorizationRuleSuggestion[]>(() =>
    this.featureEnabled() ? (this.suggestionsQuery.data() ?? []) : [],
  );
  readonly activeSuggestionCount = computed(() => this.activeSuggestions().length);

  suggestionForTransaction(transaction: RuleBackfillCandidate) {
    return findSuggestionForTransaction(this.activeSuggestions(), transaction);
  }
}
